import Link from '@tiptap/extension-link';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { getAttributes } from '@tiptap/react';

import { theme } from 'shared/utils/theme';

/**
 * This extension is used to override the built-in handleClickLink plugin from the Link extension.
 * With the built-in plugin there was a bug that prevented opening a link in the same tab
 * if the editor containing the link was editable.
 * It was fixed by using '_self' as a target if the original target is an empty string.
 */

const DEFAULT_LINK_COLOR = theme.colors?.accent?.[6];

export const CustomLink = Link.extend({
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('handleClickLink'),
        props: {
          handleClick: (view, pos, event) => {
            if (event.button !== 0) {
              return false;
            }

            const eventTarget = event.target as HTMLElement;

            if (eventTarget.nodeName !== 'A') {
              return false;
            }

            const attrs = getAttributes(view.state, 'link');
            const link = event.target as HTMLLinkElement;

            const href = link?.href ?? attrs.href;
            const target = link?.target ?? attrs.target;

            if (link && href) {
              if (view.editable) {
                window.open(href, target ? target : '_self');
              }

              return true;
            }

            return false;
          }
        }
      })
    ];
  },

  addCommands() {
    const parentCommands = this.parent?.();

    return {
      ...parentCommands,

      setLink:
        attrs =>
        ({ chain, state }) => {
          const { from, to, empty } = state.selection;
          if (empty) {
            return false;
          }

          const linkType = state.schema.marks[this.name];
          const hasLinkAlready = state.doc.rangeHasMark(from, to, linkType);

          let ch = chain().extendMarkRange(this.name).setMark(this.name, attrs);

          if (!hasLinkAlready) {
            ch = ch.setMark('underline').setMark('textStyle', { color: DEFAULT_LINK_COLOR });
          }

          return ch.run();
        },
      unsetLink:
        () =>
        ({ chain, state }) => {
          let ch = chain().extendMarkRange('link').unsetMark('link');

          const { color } = getAttributes(state, 'textStyle');
          const hasUnderline = !!getAttributes(state, 'underline');

          if (hasUnderline) {
            ch = ch.unsetMark('underline');
          }
          if (color === DEFAULT_LINK_COLOR) {
            ch = ch.unsetMark('textStyle');
          }

          return ch.run();
        }
    };
  }
});
